/* Circuit Breakers hub business-hour call availability. */

const HUB_BUSINESS_TIME_ZONE = 'America/New_York';
const HUB_BUSINESS_OPEN_MINUTES = 8 * 60;
const HUB_BUSINESS_CLOSE_MINUTES = 17 * 60;
const HUB_BUSINESS_WEEKDAYS = new Set(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
let hubEasternFormatter;

function hubDateKey(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function hubShiftedDateKey(year, month, day, offset) {
  const date = new Date(Date.UTC(year, month - 1, day + offset));
  return hubDateKey(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

function hubObservedFixedHolidayKey(year, month, day) {
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  if (weekday === 6) return hubShiftedDateKey(year, month, day, -1);
  if (weekday === 0) return hubShiftedDateKey(year, month, day, 1);
  return hubDateKey(year, month, day);
}

function hubNthWeekdayDateKey(year, month, weekday, occurrence) {
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const day = 1 + ((weekday - firstWeekday + 7) % 7) + ((occurrence - 1) * 7);
  return hubDateKey(year, month, day);
}

function hubLastWeekdayDateKey(year, month, weekday) {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const lastWeekday = new Date(Date.UTC(year, month - 1, lastDay)).getUTCDay();
  return hubDateKey(year, month, lastDay - ((lastWeekday - weekday + 7) % 7));
}

function hubFederalHolidayKeys(year) {
  const keys = new Set();

  [year - 1, year, year + 1].forEach((holidayYear) => {
    [
      [1, 1],
      [7, 4],
      [11, 11],
      [12, 25]
    ].forEach(([month, day]) => {
      keys.add(hubObservedFixedHolidayKey(holidayYear, month, day));
    });

    if (holidayYear >= 2021) {
      keys.add(hubObservedFixedHolidayKey(holidayYear, 6, 19));
    }

    keys.add(hubNthWeekdayDateKey(holidayYear, 1, 1, 3));
    keys.add(hubNthWeekdayDateKey(holidayYear, 2, 1, 3));
    keys.add(hubLastWeekdayDateKey(holidayYear, 5, 1));
    keys.add(hubNthWeekdayDateKey(holidayYear, 9, 1, 1));
    keys.add(hubNthWeekdayDateKey(holidayYear, 10, 1, 2));
    keys.add(hubNthWeekdayDateKey(holidayYear, 11, 4, 4));
  });

  return keys;
}

function hubEasternClock(date) {
  try {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

    if (!hubEasternFormatter) {
      hubEasternFormatter = new Intl.DateTimeFormat('en-US-u-hc-h23', {
        timeZone: HUB_BUSINESS_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      });
    }

    const values = {};
    hubEasternFormatter.formatToParts(date).forEach((part) => {
      if (part.type !== 'literal') values[part.type] = part.value;
    });

    const clock = {
      year: Number(values.year),
      month: Number(values.month),
      day: Number(values.day),
      weekday: values.weekday,
      hour: Number(values.hour),
      minute: Number(values.minute),
      second: Number(values.second)
    };
    const numericParts = [clock.year, clock.month, clock.day, clock.hour, clock.minute, clock.second];
    if (!numericParts.every(Number.isFinite) || !HUB_BUSINESS_WEEKDAYS.has(clock.weekday) && !['Sat', 'Sun'].includes(clock.weekday)) {
      return null;
    }
    return clock;
  } catch (error) {
    return null;
  }
}

function hubIsBusinessCallAvailable(date = new Date()) {
  const clock = hubEasternClock(date);
  if (!clock || !HUB_BUSINESS_WEEKDAYS.has(clock.weekday)) return false;

  const dateKey = hubDateKey(clock.year, clock.month, clock.day);
  if (hubFederalHolidayKeys(clock.year).has(dateKey)) return false;

  const minutes = (clock.hour * 60) + clock.minute;
  return minutes >= HUB_BUSINESS_OPEN_MINUTES && minutes < HUB_BUSINESS_CLOSE_MINUTES;
}

function hubNextMinuteDelay(date = new Date()) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 60000;
  return Math.max(250, 60050 - (date.getSeconds() * 1000) - date.getMilliseconds());
}

if (typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined') {
  class HubBreakerBrowse extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.businessCallLink = this.querySelector('[data-hub-business-call]');
      this.quoteLink = this.querySelector('.hub-path__rescue-primary');
      if (this.businessCallLink) {
        this.onBusinessCallPageShow = () => this.refreshBusinessCallAvailability();
        this.onBusinessCallVisibilityChange = () => {
          if (document.visibilityState === 'visible') this.refreshBusinessCallAvailability();
        };
        window.addEventListener('pageshow', this.onBusinessCallPageShow);
        document.addEventListener('visibilitychange', this.onBusinessCallVisibilityChange);
        this.refreshBusinessCallAvailability();
      }
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      if (this.businessCallTimer) window.clearTimeout(this.businessCallTimer);
      if (this.onBusinessCallPageShow) window.removeEventListener('pageshow', this.onBusinessCallPageShow);
      if (this.onBusinessCallVisibilityChange) {
        document.removeEventListener('visibilitychange', this.onBusinessCallVisibilityChange);
      }
      this.businessCallTimer = null;
      this.initialized = false;
    }

    refreshBusinessCallAvailability() {
      this.updateBusinessCallAvailability();
      this.scheduleBusinessCallRefresh();
    }

    scheduleBusinessCallRefresh() {
      if (this.businessCallTimer) window.clearTimeout(this.businessCallTimer);
      this.businessCallTimer = window.setTimeout(() => {
        this.businessCallTimer = null;
        if (!this.initialized) return;
        this.updateBusinessCallAvailability();
        this.scheduleBusinessCallRefresh();
      }, hubNextMinuteDelay());
    }

    updateBusinessCallAvailability(date = new Date()) {
      if (!this.businessCallLink) return false;
      const available = hubIsBusinessCallAvailable(date);

      if (!available && document.activeElement === this.businessCallLink && this.quoteLink) {
        this.quoteLink.focus();
      }
      this.businessCallLink.hidden = !available;
      if (available) this.businessCallLink.removeAttribute('aria-hidden');
      else this.businessCallLink.setAttribute('aria-hidden', 'true');
      return available;
    }
  }

  if (!customElements.get('hub-breaker-browse')) {
    customElements.define('hub-breaker-browse', HubBreakerBrowse);
  }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    hubEasternClock,
    hubFederalHolidayKeys,
    hubIsBusinessCallAvailable,
    hubNextMinuteDelay
  };
}
